/**
 * ⚠️  INTERNAL USE ONLY — NOT FOR PUBLIC ACCESS
 * Route: /admin/phone-repair-preview
 * Password-gated. noindex. Not linked from any public page.
 *
 * Preview hub for the phone repair section before public launch.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ExternalLink, CheckCircle, Clock,
  Smartphone, HardDrive, MapPin, Settings, Star, Shield,
} from '../../lib/icons';
import { AdminLoginGate, isAdminAuthed } from '../../components/AdminLoginGate';
import SEOHead from '../../components/SEOHead';
import { PHONE_REPAIR_AREAS } from '../../data/phone-repair-areas';


const STATUS_ITEMS = [
  { label: 'Phone Repair Home Page', path: '/phone-repair', status: 'live', icon: Smartphone },
  { label: 'City Pages (10 cities)', path: null, status: 'live', icon: MapPin },
  { label: 'Data Recovery Page', path: '/phone-repair/data-recovery', status: 'live', icon: HardDrive },
  { label: 'Screen Protection Page', path: '/phone-repair/screen-protection', status: 'live', icon: Shield },
  { label: 'Scheduler — Phone Categories', path: '/book-repair', status: 'live', icon: Settings },
  { label: 'Phone Services (Seed Data)', path: '/admin/services', status: 'live', icon: CheckCircle },
  { label: 'Buyback (existing /buyback)', path: '/buyback', status: 'live', icon: Star },
  { label: 'Admin Services (iPhone filter)', path: '/admin/services', status: 'live', icon: Settings },
  { label: 'Public Nav Link', path: null, status: 'pending', icon: Clock },
  { label: 'Google Sheets — Phone Tab', path: null, status: 'pending', icon: Clock },
];

const PreviewDashboard = () => {
  const live  = STATUS_ITEMS.filter(i => i.status === 'live').length;
  const total = STATUS_ITEMS.length;

  return (
    <div className="min-h-screen bg-[#050B14] font-sans">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 rounded-md bg-amber-500/20 border border-amber-500/30 text-amber-400 text-xs font-bold uppercase tracking-wide">Internal Preview Only</span>
            </div>
            <h1 className="text-white text-2xl font-bold">Phone Repair Section Preview</h1>
            <p className="text-slate-500 text-sm mt-0.5">Build progress and quick links — not public-facing</p>
          </div>
          <div className="text-right">
            <p className="text-[#10B981] font-bold text-2xl">{live}/{total}</p>
            <p className="text-slate-500 text-xs">items complete</p>
          </div>
        </div>

        {/* Admin nav links — phone repair section only */}
        <div className="flex gap-2 mb-8 flex-wrap">
          <Link to="/admin/services" className="px-3 py-1.5 rounded-lg bg-[#0A1628] border border-slate-700 text-slate-400 text-xs hover:text-white hover:border-slate-600 transition-colors">
            ← Admin Services
          </Link>
          <Link to="/admin/products" className="px-3 py-1.5 rounded-lg bg-[#0A1628] border border-slate-700 text-slate-400 text-xs hover:text-white hover:border-slate-600 transition-colors">
            Admin Products
          </Link>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Build Status */}
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-[#0A1628] border border-slate-700/50 rounded-2xl p-5">
              <h2 className="text-white font-bold mb-4">Build Status</h2>
              <div className="space-y-2">
                {STATUS_ITEMS.map(item => (
                  <div key={item.label} className="flex items-center gap-3 py-2 border-b border-slate-800/60 last:border-b-0">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${item.status === 'live' ? 'bg-[#10B981]' : 'bg-amber-400'}`} />
                    <div className="flex-1">
                      <span className="text-sm text-slate-300">{item.label}</span>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      item.status === 'live'
                        ? 'bg-[#10B981]/15 text-[#10B981]'
                        : 'bg-amber-500/15 text-amber-400'
                    }`}>
                      {item.status === 'live' ? '✓ Built' : '⏳ Pending'}
                    </span>
                    {item.path && (
                      <Link to={item.path} target="_blank"
                        className="text-[#0EA5E9] hover:text-white transition-colors">
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* City pages */}
            <div className="bg-[#0A1628] border border-slate-700/50 rounded-2xl p-5">
              <h2 className="text-white font-bold mb-4">City Page Previews</h2>
              <div className="grid sm:grid-cols-2 gap-2">
                {PHONE_REPAIR_AREAS.map(area => (
                  <Link
                    key={area.slug}
                    to={`/phone-repair/${area.slug}`}
                    className="flex items-center justify-between px-3 py-2.5 bg-[#050B14] border border-slate-800/60 rounded-lg hover:border-[#0EA5E9]/30 transition-colors group"
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-1.5 h-1.5 rounded-full ${area.isSameDay ? 'bg-[#10B981]' : 'bg-amber-400'}`} />
                      <span className="text-slate-300 text-sm group-hover:text-white">{area.city}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-600">{area.isSameDay ? 'Same-day' : 'Appt'}</span>
                      <ExternalLink className="h-3 w-3 text-slate-600 group-hover:text-[#0EA5E9]" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Quick links */}
            <div className="bg-[#0A1628] border border-slate-700/50 rounded-2xl p-5">
              <h3 className="text-white font-bold mb-3 text-sm">Preview Pages</h3>
              <div className="space-y-1.5">
                {[
                  { label: 'Phone Repair Home', path: '/phone-repair', icon: Smartphone },
                  { label: 'Data Recovery', path: '/phone-repair/data-recovery', icon: HardDrive },
                  { label: 'Screen Protection', path: '/phone-repair/screen-protection', icon: Shield },
                  { label: 'Booking (Phone Flow)', path: '/book-repair', icon: Settings },
                  { label: 'Buyback', path: '/buyback', icon: Star },
                ].map(link => (
                  <Link key={link.path} to={link.path}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-[#050B14] text-slate-400 hover:text-[#0EA5E9] transition-colors text-sm">
                    <link.icon className="h-3.5 w-3.5" />
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Launch notes */}
            <div className="bg-[#0A1628] border border-amber-500/20 rounded-2xl p-5">
              <h3 className="text-amber-400 font-bold mb-2 text-sm">Before Public Launch</h3>
              <ul className="space-y-1.5">
                {[
                  'Add phone services to Google Sheet',
                  'Set accurate pricing per model',
                  'Add /phone-repair to main nav',
                  'Set up RepairShopr categories for phone repairs',
                  'Test full booking flow end-to-end',
                  'Review all city page SEO content',
                  'Verify Temecula location messaging',
                ].map(note => (
                  <li key={note} className="flex items-start gap-1.5 text-xs text-slate-400">
                    <span className="text-amber-400 mt-0.5">·</span>
                    {note}
                  </li>
                ))}
              </ul>
            </div>

            {/* Segment legend */}
            <div className="bg-[#0A1628] border border-slate-700/50 rounded-2xl p-5">
              <h3 className="text-white font-bold mb-2 text-sm">City Legend</h3>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#10B981]" /> Temecula region — same-day
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400" /> North County SD — appointment
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminPhoneRepairPreviewPage = () => {
  const [authed, setAuthed] = useState(isAdminAuthed());

  return (
    <>
      <SEOHead title="Phone Repair Admin Preview" description="" noIndex />
      {authed ? <PreviewDashboard /> : <AdminLoginGate onAuth={() => setAuthed(true)} />}
    </>
  );
};

export default AdminPhoneRepairPreviewPage;
